// Company intelligence's client (module 10 U52 to U59; B517).
//
// MIRROR OF the page shapes in apps/api/src/intel/types.ts, field for field. A copy and not an import
// because the two packages do not depend on one another; apps/api/src/intel/mirror.test.ts reads both
// files and fails when a field exists on one side only (the industry page's lesson: a mock that
// answered a shape the API did not blanked a live page).
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';

export interface LineView {
  id: string;
  text: string;
  people: string[];
  isNew: boolean;
  addedAt: string;
  happenedAt: string | null;
  sources: { mail: number; chat: number; call: number };
}

export interface CompanyView {
  organisationId: string;
  name: string;
  label: string;
  owner: string | null;
  lines: LineView[];
}

export interface HandOnView extends LineView {
  handedTo: string | null;
}

export interface ReadoutView {
  day: string | null;
  coversFrom: string | null;
  coversTo: string | null;
  writtenAt: string | null;
  addedAt: string[];
  newSinceMorning: number;
  conversations: number;
  handOns: HandOnView[];
  companies: CompanyView[];
  days: string[];
  history: DaySummary[];
  failed: string | null;
}

export interface DaySummary {
  day: string;
  /** null on a day older than the last fortnight's, whose counts the page does not draw (B2325). */
  companies: number | null;
  handOns: number | null;
  conversations: number;
}

export interface SourceView {
  signalId: string;
  kind: string;
  at: string;
  subject: string | null;
  from: string | null;
  text: string | null;
  meetingId: string | null;
}

export type CompanyIntel = ReadoutView & { store: { tenantId: string; name: string } };

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json().then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null)).catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

let shared: { at: number; p: Promise<CompanyIntel> } | null = null;

/**
 * The newest read-out, or a named day's. The bar and the three lines on triage ask for the newest at
 * the same moment; they share one request for a minute rather than making two.
 */
export function companyIntel(day: string | null = null, opts: { fresh?: boolean } = {}): Promise<CompanyIntel> {
  if (day) return fetch(`/api/intel/company?day=${encodeURIComponent(day)}`, { credentials: 'same-origin' }).then((r) => json<CompanyIntel>(r));
  if (!opts.fresh && shared && Date.now() - shared.at < 60_000) return shared.p;
  const p = fetch('/api/intel/company', { credentials: 'same-origin' }).then((r) => json<CompanyIntel>(r));
  shared = { at: Date.now(), p };
  p.catch(() => { if (shared?.p === p) shared = null; });
  return p;
}

/** What a line was written from (U58: a line is read only and opens its sources). */
export function lineSources(lineId: string): Promise<{ sources: SourceView[] }> {
  return fetch(`/api/intel/company/lines/${encodeURIComponent(lineId)}/sources`, { credentials: 'same-origin' }).then((r) => json<{ sources: SourceView[] }>(r));
}
